import { useEffect, useMemo, useRef } from 'react';

import { buttonVariants } from 'components/partials/button/button';
import Loader from 'components/partials/loader/loader';
import Notification from 'components/partials/notification/notification.helper';

import {
  CLIENT_SUCCESS_MAILTO,
  CLIENT_SUPPORT_EMAIL,
} from 'constants/contacts';

import { useOkta } from 'utils/okta/use-okta';
import {
  buildOktaErrorDiagnostics,
  clearOktaTransaction,
  isAuthorizeRejection,
} from 'utils/okta/okta-auth-errors';

import { trackOktaCallbackError } from 'containers/login/okta-widget/analytics/okta-login-analytics';
import LoginLayout from 'containers/login/login-layout';

const LOGIN_ISSUE_FLASH_DURATION_MS = 15000;

export interface LoginCallbackErrorProps {
  error: Error;
  callbackSearch: string;
}

const LoginCallbackError = ({
  error,
  callbackSearch,
}: LoginCallbackErrorProps): React.JSX.Element | null => {
  const oktaAuth = useOkta();
  const headingRef = useRef<HTMLHeadingElement>(null);

  const isRejection = useMemo(
    () => isAuthorizeRejection(callbackSearch),
    [callbackSearch]
  );
  const diagnostics = useMemo(
    () => buildOktaErrorDiagnostics(error, oktaAuth, callbackSearch),
    [error, oktaAuth, callbackSearch]
  );

  useEffect(() => {
    trackOktaCallbackError(diagnostics);
    console.error('Okta login callback failed:', diagnostics);

    clearOktaTransaction(oktaAuth);

    if (isRejection) return;

    Notification.flash(
      'Log-in Issue',
      'info',
      "We couldn't complete your sign-in. Please try again.",
      LOGIN_ISSUE_FLASH_DURATION_MS
    );

    window.location.replace('/login');
  }, [diagnostics, isRejection, oktaAuth]);

  useEffect(() => {
    if (isRejection) {
      headingRef.current?.focus();
    }
  }, [isRejection]);

  if (!isRejection) {
    return <Loader loading fullScreen />;
  }

  return (
    <LoginLayout>
      <div className="flex flex-col items-center text-center">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="font-h-1 text-navy-800 font-semibold text-2xl outline-none"
        >
          We couldn't sign you in
        </h1>
        <p className="mt-4 text-gray-700">
          There was an issue logging you in. If the issue persists, please
          contact your administrator or MARKETview support: &nbsp;
          <a href={CLIENT_SUCCESS_MAILTO} className="text-blue-700 underline">
            {CLIENT_SUPPORT_EMAIL}
          </a>
        </p>
        {diagnostics.errorCode && (
          <p className="mt-2 text-sm text-gray-500">
            Error code: {diagnostics.errorCode}
          </p>
        )}
        <a
          href="/login"
          className={buttonVariants({
            variant: 'reserve-text-link',
            size: 'md',
            className: 'mt-6',
          })}
        >
          Back to log in
        </a>
      </div>
    </LoginLayout>
  );
};

export default LoginCallbackError;
